import Vue from "vue";
import {
  CanvasTypes,
  disabledTools,
  viewerInteractionsTools,
  viewerMeasurementsTools,
  viewerAnnotationTools,
  viewerViewportActions,
  viewerViewportOptions,
  customerSpecificMeasurementTools
} from "@/js/preferences";
import { EventBus } from "@/js/eventBus";
import {
  removeAnnotationByUuid,
  updateOrInsertAnnotationByUuid
} from "@/js/utils.viewerAnnotations";
import {
  applyGtWorkflowStatus,
  GT_STATUS_NOT_STARTED,
  resetStudyCompletionIfNeeded
} from "@/js/gt-workflow-status";
import { DEFAULT_VR_COLORMAP_ID } from "@/js/vr/vrColormaps";

// Central viewer store: layout, tool state, annotations and collab presence signals.
const allowToDisableTools = false;

// Factory form avoids cross-test/session shared state in Vuex module reuse.
const getInitialState = () => ({
  activeViewportIndex: -1,
  colormapId: null,
  // Colormap for the dedicated VR volume view (see js/vr/vrColormaps.js)
  vrColormapId: DEFAULT_VR_COLORMAP_ID,
  // True while the dedicated VR pane is initializing/rendering
  vrLoading: false,
  layout: null,
  data: [], // data grouped by studies
  options: viewerViewportOptions || [],
  showGroundTruthPanel: true,
  previousStackLayout: null,
  previousStackViewports: null,
  selectedTool: null,
  reportMode: false,
  reportEditing: null,
  activeRightPanel: null,
  discussionPanelOpen: false,
  navRightWidth: 0,
  tools: {
    interaction: viewerInteractionsTools,
    measurement: viewerMeasurementsTools.map(tool => {
      const replacementTool = customerSpecificMeasurementTools.find(
        replacement => replacement.name === tool.name
      );
      if (replacementTool) {
        // Update tool configuration
        return {
          ...tool,
          configuration: {
            ...tool.configuration,
            ...replacementTool.configuration
          }
        };
      } else {
        return tool;
      }
    }),
    annotation: viewerAnnotationTools
  },
  viewportActions: viewerViewportActions,
  viewports: [],
  server: false,
  predictions: {
    predictors: [],
    jobs: {}
  },
  activeMask: null,
  annotations: {},
  layoutSwitching: false,
  shareId: null,
  // Granular permissions of the share link the current guest opened (null for
  // workspace members). Populated on claim; gates guest-only UI actions.
  sharePermissions: null,
  collabTooltip: "",
  toolsToEnableForCachedSeries: [],
  SAM2Initialized: false,
  // Incremented after SAM2 init completes to trigger one-time mask reset.
  sam2InitToken: 0,
  sam2InitHandledToken: 0,
  analysisResults: null,
  analysisLandmarks: null,
  SAM2ObjectPreviews: {},
  AIRange: [1, -1],
  pendingSliceRestore: null,
  activeAnnotationTool: null,
  customAnnotationEntries: [],
  hiddenGroundTruthAnnotations: {},
  groundTruthMeta: {
    breastDensity: { study: "", splitByLaterality: false, L: "", R: "" },
    diagnosis: { study: "", splitByLaterality: false, L: "", R: "" },
    validation: { status: GT_STATUS_NOT_STARTED, isCompleted: false }
  },
  groundTruthMetaArtifactId: null,
  // seriesId -> percentage, used by viewer download progress UI
  downloadProgress: {},
  // Per-image download progress for large HTTP-streamed images.
  // Keyed as { [seriesId]: { [imageId]: percentage } }
  imageDownloadProgress: {},
  // { seriesId: { userId: timeoutId } } — auto-expires after 5s
  annotatingUsers: {},
  // { annotationId: { userId, label, color, lastSeen } }
  activeEditors: {}
});

const clearAnnotatingTimeouts = state => {
  Object.values(state.annotatingUsers).forEach(seriesMap => {
    Object.values(seriesMap).forEach(tid => clearTimeout(tid));
  });
};

const clearCaseTransientState = state => {
  clearAnnotatingTimeouts(state);
  const fresh = getInitialState();
  [
    "activeViewportIndex",
    "colormapId",
    "vrColormapId",
    "vrLoading",
    "layout",
    "previousStackLayout",
    "previousStackViewports",
    "selectedTool",
    "reportMode",
    "reportEditing",
    "viewports",
    "activeMask",
    "annotations",
    "layoutSwitching",
    "shareId",
    "collabTooltip",
    "toolsToEnableForCachedSeries",
    "SAM2Initialized",
    "sam2InitToken",
    "sam2InitHandledToken",
    "analysisResults",
    "analysisLandmarks",
    "SAM2ObjectPreviews",
    "AIRange",
    "pendingSliceRestore",
    "activeAnnotationTool",
    "customAnnotationEntries",
    "hiddenGroundTruthAnnotations",
    "groundTruthMeta",
    "groundTruthMetaArtifactId",
    "downloadProgress",
    "imageDownloadProgress",
    "annotatingUsers",
    "activeEditors"
  ].forEach(key => {
    state[key] = fresh[key];
  });
  Vue.set(state.predictions, "jobs", {});
};

// Called as cleanup when there are no data selected.
// Call clearSeriesData and deleteViewport for viewports not in data anymore
const clearViewportsStore = (state, data) => {
  state.viewports.forEach(v => {
    if (!v) {
      return;
    }

    const study = data.find(d => d.id == v.study);
    if (study) {
      const series = study.series.find(d => d.id == v.series);
      if (!series) {
        // cleanings for v
        Vue.prototype.$dicom.clearSeriesData(v.paneId, v.series);
        Vue.prototype.$dicom.deleteViewport(v.paneId);
        Vue.set(state, "annotations", {});
        Vue.set(state, "toolsToEnableForCachedSeries", []);
      }
    } else {
      // cleanings for v
      Vue.prototype.$dicom.clearSeriesData(v.series);
      Vue.prototype.$dicom.deleteViewport(v.paneId);
      Vue.set(state, "toolsToEnableForCachedSeries", []);
    }
  });
};

// Recursively count the number of panes of a layout.
const countPanes = layout => {
  return layout.panes.reduce((sum, pane) => {
    if (pane.panes) {
      sum += countPanes(pane);
    } else {
      sum++;
    }
    return sum;
  }, 0);
};

// Recursively flatten layout tree into rendered panes list.
const getRenderedPanes = layout => {
  let res = layout.panes.reduce((panes, pane) => {
    if (pane.panes) {
      panes = panes.concat(getRenderedPanes(pane));
    } else {
      panes = panes.concat(pane);
    }
    return panes;
  }, []);

  return res;
};

// Set active viewport to the first not null viewport index or -1
const resetActiveViewport = state => {
  state.activeViewportIndex = state.viewports.findIndex(v => !!v);
};

/*
// Check if a tool is enabled for a specific canvas
const isToolEnabled = (canvasType, toolName) => {
  const enabledTools = [
    ...viewerInteractionsTools,
    ...viewerMeasurementsTools
  ].filter(({ name }) => !disabledTools[canvasType].find(v => v == name));

  return enabledTools.find(({ name }) => name == toolName);
};
*/

// Activate a tool
const activateTool = (tool, panes, options) => {
  panes.forEach(pane => {
    const isDisabled = disabledTools[pane.type].find(v => v == tool.name);
    if (isDisabled) {
      return;
    }

    switch (pane.type) {
      case CanvasTypes.stack: {
        Vue.prototype.$dicom.activateTool(tool, [pane.id], options);
        break;
      }

      case CanvasTypes.mpr: {
        EventBus.$emit("mpr-activate-tool", tool.name);
        break;
      }

      case CanvasTypes.vr: {
        EventBus.$emit("vr-activate-tool", tool.name);
        break;
      }
    }
  });
};

// Disable tool on panes
const disableTool = (tool, panes) => {
  panes.forEach(pane => {
    const isDisabled = disabledTools[pane.type].find(v => v == tool.name);
    if (isDisabled) {
      return;
    }

    switch (pane.type) {
      case CanvasTypes.stack: {
        Vue.prototype.$dicom.disableTool(tool, [pane.id]);
        break;
      }

      /*
      case CanvasTypes.mpr: {
        EventBus.$emit("mpr-disable-tool", tool.name);
        break;
      }

      case CanvasTypes.vr: {
        EventBus.$emit("vr-disable-tool", tool.name);
        break;
      }
      */
    }
  });
};

export default {
  namespaced: true,
  state: getInitialState(),
  getters: {
    numberOfSeries: state =>
      state.data.reduce((sum, study) => sum + (study.series ? study.series.length : 0), 0),
    // Active viewport layout name and pane type
    activeLayoutPane: (state, getters) => {
      const viewport = getters.viewport(state.activeViewportIndex);
      if (viewport && state.layout) {
        const pane =
          state.layout.panes.find(p => p.id == viewport.paneId) || {};
        return { layoutName: state.layout.name, canvasType: pane.type };
      }
      return null;
    },
    // Number of panes of the current layout
    numberOfPanes: state => countPanes(state.layout),
    analysisResults: state => state.analysisResults,
    analysisLandmarks: state => state.analysisLandmarks,
    // Data structure used to render the navigation panel
    panel: state => {
      // Add a patient layer on top of state.data
      return state.data.reduce((result, study) => {
        // omit patient data from study
        // eslint-disable-next-line no-unused-vars
        let { patient, ..._study } = study;
        _study.series = _study.series.map(s => ({
          id: s.id,
          promise: s.promise
        }));

        if (!result[study.patient.id]) {
          result[study.patient.id] = {
            ...study.patient,
            studies: [_study]
          };
        } else {
          result[study.patient.id].studies.push(_study);
        }
        return result;
      }, {});
    },
    // Get series information from state.data
    seriesData: state => viewport => {
      if (!viewport) {
        return;
      }

      const { study, series } = viewport;
      let result;

      try {
        result = state.data.find(d => d.id == study);
        const workgroup = result.workgroup;

        result = result.series.find(s => s.id == series);
        result.workgroup = workgroup;
      } catch (error) {
        console.error(error);
      }
      if (!result.labels) result.labels = [];

      return result;
    },
    studyData: state => viewport => {
      if (!viewport) {
        return;
      }

      const { study } = viewport;
      return state.data.find(d => d.id == study);
    },
    activeViewportStackReady: (state, getters) => {
      if (state.activeViewportIndex < 0) {
        return false;
      }
      const viewport = state.viewports[state.activeViewportIndex];
      const data = getters.seriesData(viewport);
      // series upload is completed and series is fully downloaded from server
      return data ? data.upload_completed && data.download_completed : false;
    },
    // Get viewport by index
    viewport: state => index => state.viewports[index],
    // Get viewport index by series id
    viewportIndex: state => id =>
      state.viewports.findIndex((v = {}) => v.series == id),
    isCustomAnnotationActive: state => state.activeAnnotationTool !== null,
    selectedToolIndex: state => type =>
      state.activeAnnotationTool
        ? -1
        : state.selectedTool
        ? state.tools[type].findIndex(
            ({ name }) => name == state.selectedTool.name
          )
        : -1,
    predictorByJob: state => jobId => {
      const job = state.predictions.jobs[jobId];
      if (job) {
        return state.predictions.predictors.find(p => p.id === job.predictorId);
      }
    },
    jobProgress: state => jobId => {
      if (
        state.predictions.jobs[jobId] &&
        state.predictions.jobs[jobId].task_info
      ) {
        const taskInfo = state.predictions.jobs[jobId].task_info;
        return Math.round((100 * taskInfo.done) / taskInfo.total);
      } else {
        return 0;
      }
    },
    jobsBySeries: state => seriesId => {
      return Object.keys(state.predictions.jobs).reduce((list, jobId) => {
        if (state.predictions.jobs[jobId].seriesId === seriesId) {
          list.push({ ...state.predictions.jobs[jobId], id: jobId });
        }
        return list;
      }, []);
    },
    annotationForImageID: state => imageID => {
      if (state.annotations && imageID) {
        if (imageID in state.annotations) {
          return state.annotations[imageID];
        }
      }
    },
    isCollabEnabled(state, _getters, rootState, rootGetters) {
      // demo -> disabilitato
      // shared -> abilitato
      // utente non loggato -> disabilitato
      // setting del customer enabledFeatures.collaborative a false -> disabilitato
      // altrimenti disablitato
      // check root getters invece di state.auth.user.is_demo
      return (
        rootGetters["auth/isAuth"] && // loggato
        (!rootState.auth.user.is_demo || state.shareId !== null) && // non demo o link sharato
        process.env.APP_CUSTOMER.enabledFeatures.collaborative // feature abilitata nel customer file
      );
    },
    isTotsegEnabled: () => {
      return process.env.APP_CUSTOMER.enabledFeatures.autoSeg;
    },
    isManualSegEnabled: (state, _getters, rootState, rootGetters) => {
      return (
        rootGetters["auth/isAuth"] &&
        !rootState.auth.user.is_demo &&
        process.env.APP_CUSTOMER.enabledFeatures.manualSeg
      );
    },
    isDeepcEnabled: () => {
      return process.env.APP_CUSTOMER.enabledFeatures.deepc;
    },
    isShareLink: state => state.shareId !== null,
    isSAM2Initialized: state => {
      return state.SAM2Initialized;
    },
    getSAM2ObjectPreviewById: state => id => {
      return state.SAM2ObjectPreviews[id];
    },
    annotatingUsersForSeries: state => seriesId => {
      const map = state.annotatingUsers[seriesId];
      return map ? Object.keys(map).map(Number) : [];
    },
    isAnyDownloadInProgress: state => {
      const downloadProgress = state.downloadProgress;
      if (!downloadProgress) {
        return false;
      }
      return Object.values(downloadProgress).some(entry => {
        if (!entry) return false;
        const progressValue = Number(entry.value);
        return (
          Number.isFinite(progressValue) &&
          progressValue >= 0 &&
          progressValue < 100
        );
      });
    }
  },
  mutations: {
    // Replace viewer data with new data
    initData: (state, data) => {
      clearViewportsStore(state, data);
      clearCaseTransientState(state);
      state.data = data;
    },
    // Add new data to viewer data
    updateData: (state, data) => {
      state.data = [...state.data, ...data];
    },
    reset: state => {
      clearAnnotatingTimeouts(state);
      const s = getInitialState();
      Object.keys(s).forEach(key => (state[key] = s[key]));
    },
    // Set active viewport index
    setActiveViewport: (state, index) => (state.activeViewportIndex = index),
    setColormapId: (state, colormapId) => (state.colormapId = colormapId),
    setVrColormapId: (state, vrColormapId) =>
      (state.vrColormapId = vrColormapId),
    setVrLoading: (state, vrLoading) => (state.vrLoading = vrLoading),
    setServer: (state, value) => (state.server = value),
    setReportEdit: (state, reportObj) => (state.reportEditing = reportObj),
    clearReportEdit: state => (state.reportEditing = null),
    setActiveRightPanel: (state, panel) => {
      const nextPanel = ["groundTruth", "discussion"].includes(panel)
        ? panel
        : null;
      Vue.set(state, "activeRightPanel", nextPanel);
      Vue.set(state, "discussionPanelOpen", nextPanel === "discussion");
      if (!nextPanel) {
        state.navRightWidth = 0;
      } else if (state.navRightWidth <= 0) {
        state.navRightWidth = 320;
      }
    },
    setDiscussionPanelOpen: (state, value) => {
      const isOpen = !!value;
      Vue.set(state, "discussionPanelOpen", isOpen);
      Vue.set(state, "activeRightPanel", isOpen ? "discussion" : null);
      if (!isOpen) {
        state.navRightWidth = 0;
      } else if (state.navRightWidth <= 0) {
        state.navRightWidth = 320;
      }
    },
    setNavRightWidth: (state, width) => {
      state.navRightWidth = width;
      if (width <= 0) {
        Vue.set(state, "activeRightPanel", null);
        Vue.set(state, "discussionPanelOpen", false);
      }
    },
    setActiveAnnotationTool: (state, tool) => {
      state.activeAnnotationTool = tool;
    },
    clearActiveAnnotationTool: state => {
      state.activeAnnotationTool = null;
    },
    addCustomAnnotationEntry: (state, entry) => {
      state.customAnnotationEntries.push(entry);
    },
    setCustomAnnotationEntries: (state, entries) => {
      state.customAnnotationEntries = entries || [];
    },
    removeCustomAnnotationEntry: (state, index) => {
      // Avoid accidental removal when the UI passes an invalid index.
      if (
        !Number.isInteger(index) ||
        index < 0 ||
        index >= state.customAnnotationEntries.length
      ) {
        return;
      }
      state.customAnnotationEntries.splice(index, 1);
    },
    clearCustomAnnotationEntries: state => {
      state.customAnnotationEntries = [];
    },
    setGroundTruthMeta: (state, { meta, artifactId }) => {
      state.groundTruthMeta = applyGtWorkflowStatus(meta);
      state.groundTruthMetaArtifactId = artifactId;
    },
    updateGroundTruthBreastDensity: (state, breastDensity) => {
      const nextMeta = resetStudyCompletionIfNeeded({
        ...state.groundTruthMeta,
        breastDensity
      });
      state.groundTruthMeta = applyGtWorkflowStatus(nextMeta);
    },
    updateGroundTruthDiagnosis: (state, diagnosis) => {
      const nextMeta = resetStudyCompletionIfNeeded({
        ...state.groundTruthMeta,
        diagnosis
      });
      state.groundTruthMeta = applyGtWorkflowStatus(nextMeta);
    },
    updateStudyMedicalReport: (state, { studyId, medicalReport }) => {
      const study = state.data.find(
        item => Number(item?.id) === Number(studyId)
      );
      if (!study) {
        return;
      }
      Vue.set(study, "medical_report", medicalReport || "");
    },
    setOption: (state, { option, value }) => {
      const index = state.options.findIndex(({ name }) => name == option.name);
      if (index < 0) {
        console.warn(`Viewer option with name ${option.name} not found`);
        return;
      }

      Vue.set(state.options[index], "value", value);
      switch (option.type) {
        case "tool": {
          const renderedPanes = getRenderedPanes(state.layout);
          if (!renderedPanes.length) {
            break;
          }

          if (value) {
            Vue.prototype.$dicom.showTool(
              option.tool,
              renderedPanes.map(p => p.id)
            );
          } else {
            Vue.prototype.$dicom.hideTool(
              option.tool,
              renderedPanes.map(p => p.id)
            );
          }

          break;
        }
      }
    },
    initOptions: (state, payload) => {
      Object.keys(payload).forEach(key => {
        if (viewerViewportOptions.find(o => o.name === key)) {
          const index = state.options.findIndex(({ name }) => name == key);
          Vue.set(state.options[index], "value", payload[key]);
        }
      });
    },
    setShowGroundTruthPanel: (state, value) => {
      Vue.set(state, "showGroundTruthPanel", !!value);
    },
    initViewerSessionId: state => {
      const uuid = "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
        (
          c ^
          (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
        ).toString(16)
      );
      Vue.set(state, "viewerSessionId", uuid); // todo replace with session id (when session based )
    },
    // Change viewer layout
    updateLayout: (
      state,
      {
        layoutSwitching = false,
        resetViewports = true,
        restoreViewports = false,
        preferredViewports = null,
        ...data
      }
    ) => {
      let paneIndex = -1;
      let getViewportTarget = null;

      state.layoutSwitching = layoutSwitching;

      if (resetViewports) {
        // also automatically assign data to viewports
        state.viewports = [];

        // Series not already in viewports
        const preferredSeriesIds = new Set(
          (preferredViewports || [])
            .map(viewport => viewport && viewport.series)
            .filter(Boolean)
        );
        const unassignedData = state.data.reduce((result, study) => {
          const unassignedSeries = study.series.filter(
            s =>
              !preferredSeriesIds.has(s.id) &&
              !state.viewports.find((v = {}) => v.series == s.id)
          );
          if (unassignedSeries.length) {
            result.push({ ...study, series: unassignedSeries });
          }
          return result;
        }, []);

        // Assign to each panel a series stored in state.data
        let studyIndex = 0;
        let seriesIndex = 0;

        getViewportTarget = () => {
          if (state.viewports[paneIndex]) {
            // Already assigned viewport
            return state.viewports[paneIndex];
          }

          // Assign first series NOT already in viewports
          let result;
          if (unassignedData[studyIndex]) {
            const s = unassignedData[studyIndex].series[seriesIndex];
            if (unassignedData[studyIndex].series[seriesIndex]) {
              result = {
                series: s.id,
                study: unassignedData[studyIndex].id,
                patient: unassignedData[studyIndex].patient.id
              };
              seriesIndex++;
            } else {
              studyIndex++;
              seriesIndex = 0;
              return getViewportTarget();
            }
          }
          return result;
        };
      }

      if (restoreViewports) {
        state.viewports = [...state.previousStackViewports];
      }

      const iteratePanes = panes => {
        return panes.map(pane => {
          if (pane.panes) {
            return {
              ...pane,
              panes: iteratePanes(pane.panes)
            };
          } else {
            paneIndex++;

            // Assign an id to the pane
            pane.id = pane.id || `canvas-${paneIndex}`;

            // Assign data to viewport
            if (resetViewports) {
              const preferredTarget =
                Array.isArray(preferredViewports) &&
                preferredViewports[paneIndex]
                  ? preferredViewports[paneIndex]
                  : null;
              const target = preferredTarget || getViewportTarget();
              if (target) {
                Vue.set(state.viewports, paneIndex, {
                  ...target,
                  paneId: pane.id
                });
              }
            }

            // Set index to pane
            return {
              ...pane,
              index: paneIndex
            };
          }
        });
      };

      // Set layout
      state.layout = { ...data, panes: iteratePanes(data.panes) };

      // Set active viewport
      resetActiveViewport(state);
    },
    setViewports: (state, viewports) => {
      state.viewports = viewports;
    },
    updatePreviousLayout: (state, layout) =>
      (state.previousStackLayout = layout),
    updatePreviousViewports: (state, list) =>
      (state.previousStackViewports = list),
    // Update the data of a series assigned to a viewport (eg series promise resolved)
    updateSeries: (state, data) => {
      try {
        const studyData = state.data.find(s => s.id == data.study.id);
        const seriesIndex = studyData.series.findIndex(s => s.id == data.id);
        // eslint-disable-next-line no-unused-vars
        const { promise, study, ...seriesData } = {
          ...studyData.series[seriesIndex],
          ...data,
          download_completed: false
        };
        Vue.set(studyData.series, seriesIndex, seriesData);
      } catch (error) {
        console.warn(error);
      }
    },
    downloadEnded: (state, { seriesId, value = true }) => {
      const study = state.data.find(d => {
        return d.series.find(s => s.id == seriesId);
      });

      if (study) {
        const seriesData = study.series.find(s => s.id == seriesId);
        if (seriesData) {
          Vue.set(seriesData, "download_completed", value);
        }
      }
    },
    setDownloadProgress: (state, { seriesId, value, loaded, total, phase }) => {
      // Clear by passing null/undefined to avoid stale UI on stop/cancel.
      if (value === null || value === undefined) {
        if (
          state.downloadProgress &&
          Object.prototype.hasOwnProperty.call(state.downloadProgress, seriesId)
        ) {
          Vue.delete(state.downloadProgress, seriesId);
        }
        return;
      }
      Vue.set(state.downloadProgress, seriesId, { value, loaded, total, phase });
    },
    // Track per-byte download progress for individual large images (HTTP path).
    // Pass null/undefined to clear a single image entry; auto-cleans the series
    // key when no images remain.
    setImageDownloadProgress: (state, { seriesId, imageId, value }) => {
      if (value === null || value === undefined) {
        if (state.imageDownloadProgress[seriesId]) {
          Vue.delete(state.imageDownloadProgress[seriesId], imageId);
          if (Object.keys(state.imageDownloadProgress[seriesId]).length === 0) {
            Vue.delete(state.imageDownloadProgress, seriesId);
          }
        }
        return;
      }
      if (!state.imageDownloadProgress[seriesId]) {
        Vue.set(state.imageDownloadProgress, seriesId, {});
      }
      Vue.set(state.imageDownloadProgress[seriesId], imageId, value);
    },
    toggleGroundTruthAnnotationVisibility: (state, uuid) => {
      if (!uuid) {
        return;
      }
      if (state.hiddenGroundTruthAnnotations[uuid]) {
        Vue.delete(state.hiddenGroundTruthAnnotations, uuid);
      } else {
        Vue.set(state.hiddenGroundTruthAnnotations, uuid, true);
      }
    },
    clearGroundTruthAnnotationVisibility: (state, uuid) => {
      if (!uuid) {
        return;
      }
      if (state.hiddenGroundTruthAnnotations[uuid]) {
        Vue.delete(state.hiddenGroundTruthAnnotations, uuid);
      }
    },
    uploadEnded: (state, seriesId) => {
      const study = state.data.find(d => {
        return d.series.find(s => s.id == seriesId);
      });

      if (study) {
        const seriesData = study.series.find(s => s.id == seriesId);
        if (seriesData) {
          Vue.set(seriesData, "upload_completed", true);
        }
      }
    },
    // Assign a series to a different viewport
    updateViewportIndex: (state, { index, viewport }) => {
      // Clear required viewport
      if (!viewport) {
        Vue.set(state.viewports, index, undefined);
        if (state.activeViewportIndex == index) {
          resetActiveViewport(state);
        }
        return;
      }

      const currentIndex = state.viewports.findIndex(
        (v = {}) => v.series == viewport.series
      );
      if (currentIndex === index) {
        return;
      }

      // save pane id
      viewport.paneId = state.viewports[index]
        ? state.viewports[index].paneId
        : "canvas-0";

      // Remove prev viewport
      if (currentIndex >= 0) {
        Vue.set(state.viewports, currentIndex, undefined);
      }

      Vue.set(state.viewports, index, viewport);
      Vue.set(state, "activeViewportIndex", index);
    },
    updateSelectedTool: (state, tool) => {
      const renderedPanes = getRenderedPanes(state.layout);

      const previousTool = state.selectedTool
        ? { ...state.selectedTool }
        : null;
      if (tool) {
        // add tool
        state.selectedTool = tool;
        // When a GT annotation tool owns the mouse, clear it so the
        // standard tool takes over.
        if (state.activeAnnotationTool) {
          state.activeAnnotationTool = null;
        }
        activateTool(state.selectedTool, renderedPanes);
      } else if (allowToDisableTools) {
        // remove previous selection
        state.selectedTool = null;

        if (previousTool && (!tool || tool.name != previousTool.name)) {
          disableTool(previousTool, renderedPanes);

          // preserve options of default active tools
          if (previousTool.defaultActive) {
            activateTool(previousTool, renderedPanes, {});
          }
        }
      }
    },
    addLabelsToSeries: (state, { seriesId, labels }) => {
      if (labels.length === 0) {
        return;
      }

      try {
        // Find the study containing the series
        const study = state.data.find(d =>
          d.series.some(s => s.id === seriesId)
        );

        if (!study) {
          console.warn(`Study not found for seriesId ${seriesId}`);
          return;
        }

        // Find the series within the study
        const seriesData = study.series.find(s => s.id === seriesId);

        if (!seriesData) {
          console.warn(`Series data not found for seriesId ${seriesId}`);
          return;
        }

        if (!seriesData.labels) seriesData.labels = [];

        // Remove and re-add every entry already present
        seriesData.labels = seriesData.labels
          .filter(existingLabel => {
            return !labels.some(
              newLabel => newLabel.value === existingLabel.value
            );
          })
          .concat(labels);
      } catch (error) {
        console.error("Error adding label to series:", error);
      }
    },
    addMasksToSeries: (state, { seriesId, masksInfo }) => {
      if (masksInfo.length === 0) {
        return;
      }

      try {
        // Find the study containing the series
        const study = state.data.find(d =>
          d.series.some(s => s.id === seriesId)
        );

        if (!study) {
          console.warn(`Study not found for seriesId ${seriesId}`);
          return;
        }

        // Find the series within the study
        const seriesData = study.series.find(s => s.id === seriesId);

        if (!seriesData) {
          console.warn(`Series data not found for seriesId ${seriesId}`);
          return;
        }

        // Initialize masks_info array if it doesn't exist
        if (!seriesData.masks_info) {
          seriesData.masks_info = [];
        }

        // Remove and re-add every entry already present
        seriesData.masks_info = seriesData.masks_info
          .filter(existingMask => {
            return !masksInfo.some(
              newMask => newMask.value === existingMask.value
            );
          })
          .concat(masksInfo);
      } catch (error) {
        console.error("Error adding masks to series:", error);
      }
    },
    removeMasksFromSeries: (state, { seriesId, masksInfo }) => {
      if (masksInfo.length === 0) {
        return;
      }

      try {
        // Find the study containing the series
        const study = state.data.find(d =>
          d.series.some(s => s.id === seriesId)
        );

        if (!study) {
          console.warn(`Study not found for seriesId ${seriesId}`);
          return;
        }

        // Find the series within the study
        const seriesData = study.series.find(s => s.id === seriesId);

        if (!seriesData) {
          console.warn(`Series data not found for seriesId ${seriesId}`);
          return;
        }

        // Check if masks_info array exists
        if (!seriesData.masks_info) {
          return; // No masks to remove
        }

        // Remove masks with matching values
        seriesData.masks_info = seriesData.masks_info.filter(existingMask => {
          return !masksInfo.some(
            maskToRemove => maskToRemove.value === existingMask.value
          );
        });
      } catch (error) {
        console.error("Error removing masks from series:", error);
      }
    },
    initPredictors: (state, predictors) => {
      Vue.set(state.predictions, "predictors", predictors);
    },
    addJob: (state, { predictorId, jobId, seriesId }) => {
      Vue.set(state.predictions.jobs, jobId, {
        predictorId,
        seriesId
      });
    },
    setJobStatus: (state, { jobId, status }) => {
      Vue.set(state.predictions.jobs, jobId, {
        ...state.predictions.jobs[jobId], // preserve predictorId, seriesId
        ...status
      });
    },
    removeJob: (state, jobId) => {
      Vue.delete(state.predictions.jobs, jobId);
    },
    setActiveMask: (state, mask) => {
      Vue.set(state, "activeMask", mask);
    },
    setAISliderRange: (state, range) => {
      Vue.set(state, "AIRange", range);
    },
    setPendingSliceRestore: (state, payload) => {
      const seriesId = payload?.seriesId;
      const sliceId = payload?.sliceId;
      if (seriesId === null || seriesId === undefined) {
        console.warn("Pending slice restore missing seriesId.");
        Vue.set(state, "pendingSliceRestore", null);
        return;
      }
      if (!Number.isInteger(sliceId)) {
        console.warn("Pending slice restore missing valid sliceId.", {
          seriesId,
          sliceId
        });
        Vue.set(state, "pendingSliceRestore", null);
        return;
      }
      Vue.set(state, "pendingSliceRestore", {
        seriesId,
        sliceId,
        sourceLayout: payload?.sourceLayout || null,
        sourcePaneId: payload?.sourcePaneId || null
      });
    },
    clearPendingSliceRestore: state => {
      Vue.set(state, "pendingSliceRestore", null);
    },
    setSAM2Initialized: (state, SAM2Initialized) => {
      Vue.set(state, "SAM2Initialized", SAM2Initialized);
    },
    bumpSam2InitToken: state => {
      const next = (state.sam2InitToken || 0) + 1;
      Vue.set(state, "sam2InitToken", next);
    },
    setSam2InitHandledToken: (state, token) => {
      Vue.set(state, "sam2InitHandledToken", token || 0);
    },
    setAnalysisResults: (state, results) => {
      Vue.set(state, "analysisResults", results);
    },
    setAnalysisLandmarks: (state, landmarks) => {
      Vue.set(state, "analysisLandmarks", landmarks);
    },
    upsertSeriesJsonArtifact: (state, { seriesId, artifact }) => {
      if (!seriesId || !artifact) {
        return;
      }
      const matchesSeriesId = currentId => {
        if (currentId === null || currentId === undefined) {
          return false;
        }
        if (`${currentId}` === `${seriesId}`) {
          return true;
        }
        const left = Number(currentId);
        const right = Number(seriesId);
        if (Number.isFinite(left) && Number.isFinite(right)) {
          return left === right;
        }
        return false;
      };
      const matchesSeries = series => {
        if (!series) return false;
        return [
          series.id,
          series.series_id,
          series.seriesId,
          series.series_uid,
          series.seriesUid
        ].some(candidateId => matchesSeriesId(candidateId));
      };
      const study = state.data.find(item =>
        item.series.some(series => matchesSeries(series))
      );
      if (!study) {
        console.warn(`Study not found for seriesId ${seriesId}`);
        return;
      }
      const seriesData = study.series.find(series => matchesSeries(series));
      if (!seriesData) {
        console.warn(`Series data not found for seriesId ${seriesId}`);
        return;
      }
      if (!Array.isArray(seriesData.json_artifacts)) {
        Vue.set(seriesData, "json_artifacts", []);
      }
      const artifacts = seriesData.json_artifacts;
      const existing = artifacts.find(entry => {
        if (artifact.id && entry.id === artifact.id) {
          return true;
        }
        if (artifact.name && entry.name === artifact.name) {
          return true;
        }
        return false;
      });
      if (existing) {
        if (artifact.id && existing.id !== artifact.id) {
          Vue.set(existing, "id", artifact.id);
        }
        if (artifact.name && existing.name !== artifact.name) {
          Vue.set(existing, "name", artifact.name);
        }
        if (artifact.data !== undefined) {
          Vue.set(existing, "data", artifact.data);
        }
        return;
      }
      artifacts.push({
        id: artifact.id,
        name: artifact.name,
        data: artifact.data
      });
    },
    updateSeriesLabelAppearance: (
      state,
      { seriesId, labelValue, color, alpha }
    ) => {
      if (!seriesId || labelValue === undefined || labelValue === null) {
        return;
      }
      const matchesSeriesId = currentId => {
        if (currentId === null || currentId === undefined) {
          return false;
        }
        if (`${currentId}` === `${seriesId}`) {
          return true;
        }
        const left = Number(currentId);
        const right = Number(seriesId);
        if (Number.isFinite(left) && Number.isFinite(right)) {
          return left === right;
        }
        return false;
      };
      const matchesSeries = series => {
        if (!series) return false;
        return [
          series.id,
          series.series_id,
          series.seriesId,
          series.series_uid,
          series.seriesUid
        ].some(candidateId => matchesSeriesId(candidateId));
      };
      const study = state.data.find(item =>
        item.series.some(series => matchesSeries(series))
      );
      if (!study) {
        console.warn(`Study not found for seriesId ${seriesId}`);
        return;
      }
      const seriesData = study.series.find(series => matchesSeries(series));
      if (!seriesData) {
        console.warn(`Series data not found for seriesId ${seriesId}`);
        return;
      }
      const matchByValue = list =>
        Array.isArray(list)
          ? list.find(item => `${item.value}` === `${labelValue}`)
          : null;
      const updateField = (item, field, value) => {
        if (!item) return;
        Vue.set(item, field, value);
      };
      if (color !== undefined) {
        updateField(matchByValue(seriesData.labels), "color", color);
        updateField(matchByValue(seriesData.masks_info), "color", color);
        updateField(matchByValue(seriesData.surfaces_info), "color", color);
      }
      if (alpha !== undefined) {
        const surface = matchByValue(seriesData.surfaces_info);
        updateField(surface, "alpha", alpha);
        updateField(surface, "surface_alpha", alpha);
        updateField(matchByValue(seriesData.labels), "surface_alpha", alpha);
      }
    },
    setOnlineUsersOnSeries: (
      state,
      { seriesId, numOnlineUsers, onlineUsers }
    ) => {
      const study = state.data.find(item =>
        item.series.some(series => series.id === seriesId)
      );
      if (!study) return;
      const seriesIdx = study.series.findIndex(
        series => series.id === seriesId
      );
      if (seriesIdx < 0) return;
      Vue.set(study.series[seriesIdx], "numOnlineUsers", numOnlineUsers);
      Vue.set(study.series[seriesIdx], "onlineUsers", onlineUsers || []);
    },
    setUserAnnotating: (state, { seriesId, userId }) => {
      if (!state.annotatingUsers[seriesId]) {
        Vue.set(state.annotatingUsers, seriesId, {});
      }
      const existing = state.annotatingUsers[seriesId][userId];
      if (existing) {
        clearTimeout(existing);
      }
      const timeoutId = setTimeout(() => {
        if (state.annotatingUsers[seriesId]) {
          Vue.delete(state.annotatingUsers[seriesId], userId);
        }
      }, 5000);
      Vue.set(state.annotatingUsers[seriesId], userId, timeoutId);
    },
    setAnnotationEditor: (state, { annotationId, editor }) => {
      if (!annotationId || !editor) return;
      Vue.set(state.activeEditors, annotationId, {
        ...editor,
        lastSeen: editor.lastSeen || Date.now()
      });
    },
    setAnnotations: (state, { imageId, annotations }) => {
      let groupedAnnotations = {};
      for (let annObj of annotations) {
        let toolName = Object.keys(annObj)[0];
        let newAnnotation = annObj[toolName];
        if (!(toolName in groupedAnnotations)) {
          groupedAnnotations[toolName] = [];
        }
        // Check if there is an existing annotation with the same UUID
        let indexToUpdate = groupedAnnotations[toolName].findIndex(
          existingAnn => existingAnn.uuid === newAnnotation.uuid
        );

        if (indexToUpdate !== -1) {
          // Replace the existing annotation with the new one
          groupedAnnotations[toolName][indexToUpdate] = newAnnotation;
        } else {
          // If no matching UUID found, simply push the new annotation
          groupedAnnotations[toolName].push(newAnnotation);
        }
      }
      Vue.set(state.annotations, imageId, groupedAnnotations);
    },
    addAnnotation: (state, { imageId, toolName, annotation }) => {
      if (!imageId || !toolName || !annotation) {
        return;
      }
      const annotations = state.annotations;
      if (!annotations[imageId]) {
        Vue.set(state.annotations, imageId, { [toolName]: [annotation] });
        return;
      }
      const annotationForImageID = annotations[imageId];
      if (!annotationForImageID[toolName]) {
        Vue.set(annotationForImageID, toolName, [annotation]);
        Vue.set(state.annotations, imageId, annotationForImageID);
        return;
      }
      const annotationsForImageIdForTool = annotationForImageID[toolName];
      const indexOfAnnotation = annotationsForImageIdForTool.findIndex(
        item => item.uuid === annotation.uuid
      );
      if (indexOfAnnotation !== -1) {
        annotationsForImageIdForTool.splice(indexOfAnnotation, 1, annotation);
      } else {
        annotationsForImageIdForTool.push(annotation);
      }
      annotationForImageID[toolName] = annotationsForImageIdForTool;
      Vue.set(state.annotations, imageId, annotationForImageID);
    },
    removeAnnotation: (state, { imageId, toolName, annotationId }) => {
      const annotations = state.annotations;
      if (annotations && annotations[imageId]) {
        const annotationForImageID = annotations[imageId];
        if (annotationForImageID && annotationForImageID[toolName]) {
          const { removed, nextAnnotations } = removeAnnotationByUuid(
            annotationForImageID[toolName],
            annotationId
          );
          if (!removed) {
            return;
          }
          const annotationsForImageIdForTool = nextAnnotations;
          if (annotationsForImageIdForTool.length > 0) {
            annotationForImageID[toolName] = annotationsForImageIdForTool;
            Vue.set(state.annotations, imageId, annotationForImageID);
          } else {
            delete annotationForImageID[toolName];
            Vue.set(state.annotations, imageId, annotationForImageID);
          }
        }
      }
    },
    removeAnnotationById: (state, { annotationId, imageIds }) => {
      if (!annotationId) return;
      const annotations = state.annotations || {};
      const scopedImageIds = Array.isArray(imageIds) ? imageIds : Object.keys(annotations);
      scopedImageIds.forEach(imageId => {
        const annotationForImageID = annotations[imageId];
        if (!annotationForImageID || typeof annotationForImageID !== "object") {
          return;
        }
        const nextByTool = { ...annotationForImageID };
        let changed = false;
        Object.keys(annotationForImageID).forEach(toolName => {
          const list = annotationForImageID[toolName];
          if (!Array.isArray(list)) return;
          const { removed, nextAnnotations } = removeAnnotationByUuid(
            list,
            annotationId
          );
          if (!removed) return;
          changed = true;
          if (nextAnnotations.length > 0) {
            nextByTool[toolName] = nextAnnotations;
          } else {
            delete nextByTool[toolName];
          }
        });
        if (!changed) return;
        if (Object.keys(nextByTool).length > 0) {
          Vue.set(state.annotations, imageId, nextByTool);
        } else {
          Vue.delete(state.annotations, imageId);
        }
      });
    },
    resetLayoutSwitching: state => {
      state.layoutSwitching = null;
    },
    setShareId: (state, { shareId }) => {
      state.shareId = shareId;
    },
    setSharePermissions: (state, permissions) => {
      state.sharePermissions = permissions || null;
    },
    clearAnnotationStore: (state, { imageIds }) => {
      imageIds.forEach(imageId => {
        delete state.annotations[imageId];
      });
    },
    updateAnnotation: (state, { imageId, toolName, annotation }) => {
      const annotations = state.annotations;
      if (annotations && annotations[imageId]) {
        const annotationForImageID = annotations[imageId];
        if (annotationForImageID && annotationForImageID[toolName]) {
          const { nextAnnotations } = updateOrInsertAnnotationByUuid(
            annotationForImageID[toolName],
            annotation
          );
          annotationForImageID[toolName] = nextAnnotations;
          Vue.set(state.annotations, imageId, annotationForImageID);
        }
      }
    },
    setCollabTooltip: (state, { message }) => {
      state.collabTooltip = message;
    },
    setToolsToEnableForCachedSeries: (state, { toolName }) => {
      state.toolsToEnableForCachedSeries.push(toolName);
    },
    removeToolsToEnableForCachedSeries: (state, { toolNames }) => {
      const removeSet = new Set(toolNames || []);
      const filtered = state.toolsToEnableForCachedSeries.filter(
        toolName => !removeSet.has(toolName)
      );
      Vue.set(state, "toolsToEnableForCachedSeries", filtered);
    },
    setSAM2ObjectPreviewImage: (state, { id, image }) => {
      Vue.set(state.SAM2ObjectPreviews, id, image);
    },
    removeSAM2ObjectPreview: (state, id) => {
      Vue.delete(state.SAM2ObjectPreviews, id);
    }
  }
};
